import type { Theme } from '@mui/material';
import { Alert, styled } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { TaskAltOutlined } from '@mui/icons-material';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useEffect } from 'react';
import ServiceCatalog from './ServiceCatalog';
import ResilienceLayout from './ResilienceLayout';

import { ServiceTable } from './ServiceTable/ServiceTable';
import { ProductCode } from '../types';
import CbButton from '../../../components/Buttons/CbButton';
import { FEATURE_FLAG_KEYS } from '../../../config/growthbook';

export const UNAVAILABLE_PLAN_TOOLTIP =
  'This plan is no longer available. Kindly check our new plans.';

const MyServices = () => {
  const history = useHistory<{
    isServiceActivated: boolean;
    productCode: ProductCode | null;
  }>();
  const crsEnableSelfServe = useFeatureIsOn(
    FEATURE_FLAG_KEYS.CRS_ENABLE_SELF_SERVE
  );

  useEffect(() => {
    return () => {
      // clear the state when the component unmounts
      history.replace(history.location.pathname, {
        isServiceActivated: false,
        productCode: null,
      });
    };
  }, [history]);

  const handleOnClose = () => {
    history.replace(history.location.pathname, {
      isServiceActivated: false,
      productCode: null,
    });
  };

  const handleInvoicesClick = () => {
    history.push('/customer/resilience/invoices');
  };

  return (
    <ResilienceLayout>
      <div>
        <h1
          className="table-heading"
          style={{ marginTop: 0, marginBottom: '1.25rem' }}
        >
          My Services
        </h1>
        {history.location.state?.isServiceActivated && (
          <StyledAlert
            icon={
              <TaskAltOutlined
                sx={{
                  fontSize: 32,
                  color: (_theme) => _theme.palette.common.textSuccessLight,
                  ':hover': {
                    color: (_theme) => _theme.palette.common.cowbellGreen2,
                  },
                }}
              />
            }
            severity="success"
            sx={{ mb: 2 }}
            onClose={handleOnClose}
          >
            {history.location.state?.productCode === ProductCode.VRA_PRO
              ? 'Your VRA plan is successfully activated!'
              : 'Thank you for your interest! Our sales team will reach out to you shortly.'}
          </StyledAlert>
        )}
        {crsEnableSelfServe && (
          <InvoicesButtonContainer>
            <CbButton styleName="ctaButton" onClick={handleInvoicesClick}>
              View Invoices
            </CbButton>
          </InvoicesButtonContainer>
        )}
        <ServiceTable />
        <ServiceCatalog isShownInMyServices />
      </div>
    </ResilienceLayout>
  );
};

const StyledAlert = styled(Alert)(({ theme }: { theme: Theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? `${theme.palette.common.surfaceSuccess}1A`
      : theme.palette.common.surfaceSuccess,
  color: theme.palette.common.textSuccessLight,
  fontSize: '1.333rem',
  fontWeight: '600',
  textAlign: 'left',
  padding: '0.5rem 1rem',
  borderRadius: '5px',
  lineHeight: 'normal',
  boxShadow: `0 4px 6px ${
    theme.palette.mode === 'dark'
      ? theme.palette.common.darkGrey
      : theme.palette.common.lightGrey2
  }`,

  '& .MuiAlert-action svg': {
    color: theme.palette.common.gunmetalBlue,
  },
}));

const InvoicesButtonContainer = styled('div')(() => {
  return {
    display: 'flex',
    flexDirection: 'row-reverse',
    margin: '16px 0px',
  };
});

export default MyServices;
